import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormLabel from '@material-ui/core/FormLabel'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import Select from '@material-ui/core/Select'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { useState } from 'react'
import { useNotify, useRedirect, useRefresh } from 'react-admin'

import { JOB_ALREADY_IN_DESIRED_STATE_ERROR } from 'helpers/constants'
import { captureExceptionIfProduction } from 'helpers/utils'
import { transition as jobTransition } from '../../../api/jobs'
import { JobAbortReason, JobTransitions } from '../Transitions/JobFSMTransitions'
import { FSMTransitionButton, XTransitionButtonProps } from './FSMTransitionButton'

const CANCEL_REASON = {
  BROKER: 'BROKER',
  DRIVER: 'DRIVER',
  CLOUDTRUCKS: 'CLOUDTRUCKS',
}

const isJobCancelTransition = (action: any) => action === JobTransitions.cancel.transitionAction
const isJobVerifyDeliveryTransition = (action: any) =>
  action === JobTransitions.verifyDelivery.transitionAction
const isJobAbortTranstition = (action: any) => action === JobTransitions.abort.transitionAction
const isJobBidTransition = (action: any) => action === JobTransitions.bid.transitionAction
const isJobRecoverTransition = (action: any) => action === JobTransitions.recover.transitionAction

export const JobTransitionButton = ({
  record,
  transitionProps,
  classes,
  loading,
  setLoading,
}: XTransitionButtonProps) => {
  const notify = useNotify()
  const redirectTo = useRedirect()
  const refresh = useRefresh()

  /* State  */
  const [cancelReason, setCancelReason] = useState(CANCEL_REASON.BROKER)
  const [abortReason, setAbortReason] = useState('')
  const [abortCancelDetail, setAbortCancelDetail] = useState('')
  const [bidAmount, setBidAmount] = useState('')
  const [bidNote, setBidNote] = useState('')
  const [isFormInvalid, setIsFormInvalid] = useState(false)
  const [cancelReasonNote, setCancelReasonNote] = useState('')
  const [recoveryReason, setRecoveryReason] = useState('')
  const [otherRecoveryReason, setOtherRecoveryReason] = useState('')

  const { transitionAction } = transitionProps

  const isCancelTransition = isJobCancelTransition(transitionAction)
  const isVerifyDeliveryTransition = isJobVerifyDeliveryTransition(transitionAction)
  const isAbortTransition = isJobAbortTranstition(transitionAction)
  const isBidTransition = isJobBidTransition(transitionAction)
  const isRecoverTransition = isJobRecoverTransition(transitionAction)
  const className = isCancelTransition ? classes?.root : null

  const handleSubmit = () => {
    // Do not pass empty string
    const transitionCancelReason = isCancelTransition ? cancelReason : null
    const transitionAbortReason = isAbortTransition ? abortReason : null
    const transitionBidAmount = isBidTransition ? bidAmount : null
    const transitionBidNote = isBidTransition ? bidNote : null
    const transitionCancelReasonNote =
      isCancelTransition && cancelReason === CANCEL_REASON.CLOUDTRUCKS ? cancelReasonNote : null
    const abortCancelDetailTrimmed = abortCancelDetail.trim()
    const transitionAbortDetailReason =
      isAbortTransition && abortCancelDetailTrimmed !== '' ? abortCancelDetailTrimmed : null
    let transitionRecoveryReason = isRecoverTransition ? recoveryReason : null
    if (recoveryReason === 'OTHER') {
      transitionRecoveryReason = transitionRecoveryReason + ': ' + otherRecoveryReason
    }
    if (isVerifyDeliveryTransition && record.invoices.length === 0) {
      redirectTo(`/jobs/${record.id}/invoice/true`)
    } else if (isAbortTransition && transitionAbortReason === '') {
      setIsFormInvalid(true)
      notify('Abort Reason is Required', { type: 'error' })
    } else if (isBidTransition && transitionBidAmount === '') {
      setIsFormInvalid(true)
      notify('Bid Amount is Required', { type: 'error' })
    } else if (isRecoverTransition && transitionRecoveryReason === '') {
      setIsFormInvalid(true)
      notify('Recovery Reason is Required', { type: 'error' })
    } else {
      setLoading(true)
      jobTransition({
        id: record.id,
        transitionAction: transitionAction,
        cancelReason: transitionCancelReason,
        cancelReasonNote: transitionCancelReasonNote,
        abortReason: transitionAbortReason,
        abortCancelDetail: transitionAbortDetailReason,
        bidAmount: transitionBidAmount,
        bidNote: transitionBidNote,
        recoveryReason: transitionRecoveryReason,
      })
        .then(() => {
          refresh()
          setLoading(false)
          notify('Transition successful!', { type: 'success' })
        })
        .catch((error) => {
          setLoading(false)
          notify(JSON.stringify(error?.response?.data), { type: 'error' })
          const errorData = error?.response?.data
          const detail = errorData?.detail
          if (detail?.includes(JOB_ALREADY_IN_DESIRED_STATE_ERROR)) {
            refresh()
          } else {
            captureExceptionIfProduction(error)
          }
        })
    }
  }

  const handleRadioChange = (event: any) => {
    setCancelReason(event.target.value)
  }
  const handleAbortReasonChange = (event: any) => {
    setAbortReason(event.target.value)
  }
  const handleAbortCancelDetailChanege = (event: any) => {
    setAbortCancelDetail(event.target.value)
  }
  const handleBidAmountChange = (event: any) => {
    setBidAmount(event.target.value)
  }
  const handleBidNoteChange = (event: any) => {
    setBidNote(event.target.value)
  }
  const handleCancelReasonNoteChange = (event: any) => {
    setCancelReasonNote(event.target.value)
  }
  const handleRecoveryReasonChange = (event: any) => {
    setRecoveryReason(event.target.value)
  }
  const handleOtherRecoveyReasonChange = (event: any) => {
    setOtherRecoveryReason(event.target.value)
  }

  return (
    <FSMTransitionButton
      className={className}
      handleSubmit={handleSubmit}
      record={record}
      transitionProps={transitionProps}
      loading={loading}
    >
      {isCancelTransition && (
        <>
          <FormControl component="fieldset">
            <FormLabel component="legend">Cancelation Reason</FormLabel>
            <RadioGroup
              aria-label="Cancelation Reason"
              name="reason"
              value={cancelReason}
              onChange={handleRadioChange}
            >
              <FormControlLabel value={CANCEL_REASON.BROKER} control={<Radio />} label="Broker" />
              <FormControlLabel value={CANCEL_REASON.DRIVER} control={<Radio />} label="Driver" />
              <FormControlLabel value={CANCEL_REASON.CLOUDTRUCKS} control={<Radio />} label="CloudTrucks" />
            </RadioGroup>
          </FormControl>
          {cancelReason === CANCEL_REASON.CLOUDTRUCKS && (
            <FormControl fullWidth>
              <TextField
                id="cancel_reason_note"
                label="Note"
                multiline
                fullWidth
                rows={4}
                placeholder="Note"
                variant="outlined"
                style={{ marginTop: '10px' }}
                required={false}
                onChange={handleCancelReasonNoteChange}
              />
            </FormControl>
          )}
        </>
      )}
      {isAbortTransition && (
        <div>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="status_detail_label">Select Reason</InputLabel>
            <Select
              label="Select Reason"
              id="status_detail"
              labelId="status_detail_label"
              onChange={handleAbortReasonChange}
              value={abortReason}
              required
              error={isFormInvalid}
              fullWidth
            >
              {Object.keys(JobAbortReason).map((key) => (
                <MenuItem value={JobAbortReason[key].id} key={JobAbortReason[key].id}>
                  {JobAbortReason[key].name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth variant="outlined">
            <TextField
              id="abort_reason"
              label="Note"
              multiline
              fullWidth
              value={abortCancelDetail}
              rows={4}
              placeholder="Note (optional)"
              variant="outlined"
              style={{ marginTop: '10px' }}
              required={false}
              onChange={handleAbortCancelDetailChanege}
            />
          </FormControl>
        </div>
      )}
      {isBidTransition && (
        <div>
          <FormControl fullWidth style={{ flexDirection: 'row' }}>
            <TextField
              id="bid_amount"
              label="Bid Amount"
              variant="outlined"
              style={{ marginTop: '10px', width: '50%' }}
              fullWidth
              required={true}
              onChange={handleBidAmountChange}
            />
            <div style={{ marginTop: '10px', marginLeft: '10px' }}>
              {record.user_submitted_bid_min && record.user_submitted_bid_max && (
                <Typography>
                  Customer's bid: ${record.user_submitted_bid_min} - ${record.user_submitted_bid_max}
                </Typography>
              )}
              {record.load.estimated_rate_min && record.load.estimated_rate_max && (
                <Typography>
                  CT Estimate: ${record.load.estimated_rate_min} - ${record.load.estimated_rate_max}
                </Typography>
              )}
            </div>
          </FormControl>
          <FormControl fullWidth>
            <TextField
              id="bid_note"
              label="Note (optional)"
              multiline
              fullWidth
              rows={4}
              placeholder="Note (optional)"
              variant="outlined"
              style={{ marginTop: '10px' }}
              required={false}
              onChange={handleBidNoteChange}
            />
          </FormControl>
        </div>
      )}
      {isRecoverTransition && (
        <div>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="recovery_reason_label">Select Reason</InputLabel>
            <Select
              label="Select Reason"
              id="recovery_reason"
              labelId="recovery_reason_label"
              onChange={handleRecoveryReasonChange}
              value={recoveryReason}
              required
              error={isFormInvalid}
              fullWidth
            >
              <MenuItem value="ACCIDENT">Vehicle involved in an accident</MenuItem>
              <MenuItem value="BREAKDOWN">Vehicle suffered a breakdown, needs repair</MenuItem>
              <MenuItem value="HEALTH_ISSUE">Driver having a health issue</MenuItem>
              <MenuItem value="PERSONAL_EMERGENCY">Driver has a personal emergency</MenuItem>
              <MenuItem value="DRIVER_DQ">Driver disqualified due to safety reasons</MenuItem>
              <MenuItem value="DRIVER_CRIMINAL_RECORD_DQ">
                Driver disqualified due to criminal record
              </MenuItem>
              <MenuItem value="DRIVER_LAPSED_LICENSE">Driver's license has lapsed</MenuItem>
              <MenuItem value="DRIVER_LAPSED_INSURANCE">Driver's insurance has lapsed</MenuItem>
              <MenuItem value="DRIVER_LAPSED_MEDICAL_CARD">Driver's medical card has lapsed</MenuItem>
              <MenuItem value="DRIVER_SAFETY_SUSPENSION">
                Driver suspended due to poor safety performance
              </MenuItem>
              <MenuItem value="OTHER">Other reason</MenuItem>
            </Select>
          </FormControl>
          {recoveryReason === 'OTHER' && (
            <FormControl fullWidth variant="outlined">
              <TextField
                id="recovery_reason"
                label="Note"
                multiline
                fullWidth
                value={otherRecoveryReason}
                placeholder="Note (optional)"
                variant="outlined"
                style={{ marginTop: '10px' }}
                required={false}
                onChange={handleOtherRecoveyReasonChange}
              />
            </FormControl>
          )}
        </div>
      )}
    </FSMTransitionButton>
  )
}
